/*
 * Copyright (c) 2026. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
* planitemedit.ts
* 10-11-2022 - Jelmer Jellema - Spin in het Web B.V.
*
* Editor for a planblock on the planboard
*/

import {Component, inject, Input, ViewChild} from '@angular/core';
import {Planitem} from '../../models/planitem';
import {Editmodelscherm} from '../abstracts/editmodelscherm';
import {Project} from '../../models/project';
import {Functie} from '../../models/functie';
import {PlanitemFunction} from '../../models/planitem_function';
import moment from 'moment';
import {hash} from '../../app/types';
import {Personeel} from '../../models/personeel';
import {PersoneelPlanitem} from '../../models/personeel_planitem';
import {Datarecord} from '../../services/api/api.service';
import {PersoneelFunctie} from '../../models/personeel_functie';
import {cloneDeep} from 'lodash-es';
import {Leverancier} from '../../models/leverancier';
import {PersoneelAfwezigheid} from '../../models/personeel_afwezigheid';
import {Basemodel} from '../../models/basemodel';
import {KeyedTimeout} from 'sihw-ng/keyedtimeout/keyedtimeout';
import {Gebruiker} from '../../models/gebruiker';
import {ProjectFunctie} from '../../models/project_functie';
import {DateTime} from 'luxon';
import {PersoneelLabel} from '../../models/personeel_label';
import {FiltertoolFactoryService} from '../../services/filtertool/filtertool-factory.service';
import {FiltertoolInstantie} from '../../services/filtertool/filtertool-instantie';
import {PlanitemInvullingComponent} from './planitem-invulling.component';
import {Werkmaatschappij} from '../../models/werkmaatschappij';

const translated_days = {
    mon: 'maandag',
    tue: 'dinsdag',
    wed: 'woensdag',
    thu: 'donderdag',
    fri: 'vrijdag',
    sat: 'zaterdag',
    sun: 'zondag'
};

const daykeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export interface ExterneProjectInfo {
    werkmaatschappij: number;
    werkmaatschappijnaam?: string;
    project: number;
    projectnaam: string;
    projectomschrijving: string;
}

@Component({
    templateUrl: './planitemedit.html',
    styleUrls: ['./planitemedit.scss']
})

export class Planitemedit extends Editmodelscherm<typeof Planitem> {
    _starttitle = 'Planning';
    icon = 'fas fa-business-time';
    model = Planitem;

    /**
     * Referentie naar de invullingcomponent voor personeelslogica.
     */
    @ViewChild(PlanitemInvullingComponent) invullingComponent?: PlanitemInvullingComponent;

    /**
     * De lijst met functies die relevant zijn gegeven het project en de huidige selectie.
     */
    relevantFunctions: Functie[] = [];
    /**
     * De lijst met functies die op dit moment minder relevant zijn (bijv. niet standaard in het project).
     */
    irrelevantFunctions: Functie[] = [];
    /**
     * Ongefilterde kopie van de relevante functies, voor de "Alle functies" schakelaar.
     */
    private relevanteFunctiesOngefilterd: Functie[] = [];
    /**
     * Gefilterde lijst o.b.v. actieve filtertool (indien van toepassing).
     */
    private relevanteFunctiesGefilterd: Functie[] = [];
    /**
     * Toon alle functies i.p.v. de gefilterde lijst (alleen zichtbaar als er daadwerkelijk iets is weggefilterd).
     */
    negeerFilter: boolean = false;

    /**
     * De extra records die we mee-bewerken bij invulling, halen we op als het record een serie is
     */
    extraRecords: Planitem[] = null;

    /**
     * Wil de user de invulling ook bij (bepaalde) extra diensten opslaan?
     * @see extraRecords
     */
    saveExtra: boolean = false; //veiligheid: standaard false. Expliciet aanzetten. Dat maakt dit component ook 'dirty'

    /**
     * Onthoudt of de gebruiker via "Opslaan + melding" heeft gekozen om de automelding-engine te triggeren
     * @see save
     * @see savedata
     */
    private meldingBijOpslaan: boolean = false;

    /**
     * Geeft aan of er functies zijn weggefilterd t.o.v. de ongefilterde relevante set.
     */
    get functiesWeggefilterd(): boolean {
        if (!this.filtertool) {
            return false;
        }
        return (this.relevanteFunctiesGefilterd?.length || 0) < (this.relevanteFunctiesOngefilterd?.length || 0);
    }

    /**
     * UI: is er door een actieve filter (functie of personeel) iets weggefilterd?
     */
    get ietsWeggefilterd(): boolean {
        return this.functiesWeggefilterd || this.invullingComponent?.personeelWeggefilterd;
    }

    mindate: string = null;
    volLezen: boolean = false; //volle toegang?
    magPersoneelEdit: boolean = false; //mag deze user  ook personeel editen?
    magPersoneelRead: boolean = false; //mag deze user personeel lezen (en dus invulling bekijken)
    magLog: boolean = false; //en log bekijken?
    public isLeverancier = false; //speciale leveranciertoestemming of extern item
    public isUitlener = false; //externe werkmaatschappij die uitleent
    public isExternItem = false; //is dit een extern item (van een andere WM)?
    protected magExtern = false; //mag deze gebruiker iets met leveranciers en externe WM op een planitem e.d.?

    /**
     * Het id van de filtertool-instantie die toegepast moet worden (indien aanwezig). (Niet meesturen = niet toepassen)
     */
    @Input() filtertoolId: string | null = null;

    /**
     * De actuele filtertool-instantie (opgehaald via de factory) of null.
     */
    filtertool: FiltertoolInstantie | null = null;
    /**
     * Subscriptie-keyed timeout voor debounce van filterherberekening.
     */
    private readonly herberekenFilterTimeout = new KeyedTimeout();

    @Input() projects: Project[]; //the projectlist from the planbord
    /** Extra projectinfo voor externe planitems (werkmaatschappij, project id, naam, omschrijving) */
    @Input() externeProjectInfo: any = null;
    @Input() allitemsPerdate: hash<Planitem[]> = {}; //nodig voor overlapcheck
    override lookupQueries = {
        functions: {
            model: Functie,
            aclModel: Project
        },
        personeel: {
            model: Personeel,
            submodels: [PersoneelFunctie, PersoneelAfwezigheid, PersoneelLabel],
            aclModel: Project,
            suboptions: <[typeof Basemodel, hash<any>][]> [
                [PersoneelAfwezigheid,
                    {
                        //hier halen we het ruim op: alles vanaf een half jaar geleden, en in de toekomst
                        //zodat het goed cachet
                        where: {
                            $or: {
                                startdatum: {
                                    $gt: moment().subtract(6, 'months').startOf('month').format('YYYY-MM-DD')
                                },
                                einddatum: {
                                    $gt: moment().subtract(6, 'months').startOf('month').format('YYYY-MM-DD')
                                }
                            }
                        },
                        required: false
                    }
                ]
            ]
        },
        leverancier: {
            model: Leverancier,
            aclModel: Planitem
        },
        werkmaatschappij: {
            model: Werkmaatschappij,
            suboptions: <[typeof Basemodel, hash<any>][]> [
                [
                    Werkmaatschappij,
                    {
                        attributes: ['id', 'naam']
                    }
                ]
            ],
            aclModel: Project
        }
    };
    //we zetten wel de owner, defaultsubmodels e.d, voor als er een reload komt
    protected override throughOwner = Project;
    protected override defaultSubmodels: (typeof Basemodel)[] = [
        ProjectFunctie, Planitem, PlanitemFunction, PersoneelPlanitem, Gebruiker]; //zelfde als op planbord en gezien vanuit project
    planSeries = {
        create: <boolean> false,
        everyNumWeeks: <number> 1,
        weekdays: {
            mon: <boolean> false,
            tue: <boolean> false,
            wed: <boolean> false,
            thu: <boolean> false,
            fri: <boolean> false,
            sat: <boolean> false,
            sun: <boolean> false,
        },
        endDate: <string> ''
    };
    //eem debouncer voor ux-wijzigingen
    private debouncer: KeyedTimeout = new KeyedTimeout();

    // Services (bibliotheektaal aanhouden)
    private readonly filtertoolFactory = inject(FiltertoolFactoryService);

    constructor() {
        super('debug');
        this.isLeverancier = this.api.can('planitem', 'leverancier'); //speciale permissie
        this.volLezen = this.api.can('planitem', 'full');
        this.magPersoneelEdit = this.isExterneBewerker || this.api.can('personeelvast_planitem', ['full', 'project']); //leverancier krijgt sowieso alleen items te zien die die mag editen
        this.magPersoneelRead = this.magPersoneelEdit || this.api.can('personeelvast_planitem', 'read'); //dus los van readonly invulling readonly
        this.magExtern = this.api.can('planitem', 'full');
        this.magLog = true;
    }

    /**
     * Geeft true als de gebruiker een leverancier of uitlener is.
     */
    get isExterneBewerker(): boolean {
        return this.isLeverancier || this.isUitlener;
    }

    /**
     * Bepaal of dit een extern item is. Dat weten we pas na haaldata, maar we hebben de info nodig voor permissies
     */
    private checkExtern(): void {
        this.isExternItem = !!(this.record.externe_werkmaatschappij && this.record.externe_werkmaatschappij === this.api.werkmaatschappij.id);
        if (this.isExternItem) {
            this.isUitlener = true; //behandel als uitlener (externe WM)
            this.magPersoneelEdit = true; //mogen we altijd bij extern item (voor eigen personeel)
            this.magPersoneelRead = true;
            this.magLog = false; //geen log voor externe items (privacy / andere WM)
        }

        //leverancier mag minder
        if (this.isLeverancier) {
            delete this.lookupQueries.leverancier;
            this.magLog = false;
        }
        if (!this.magExtern) {
            //dan ook geen werkmaatschappij
            delete this.lookupQueries.werkmaatschappij;
        }
    }

    /**
     * Hulpje om personeelslogica in het invullingcomponent te synchroniseren.
     */
    private synchroniseerInvulling(): void {
        // Plan de aanroep naar de volgende tick zodat input-binding (zoals filtertool)
        // eerst verwerkt kan worden door Angular. Dit voorkomt te vroege synchronisatie
        // waarbij filtering nog niet actief is.
        setTimeout(() => this.invullingComponent?.synchroniseer());
    }

    /**
     * Bepaal of de externe werkmaatschappij gewijzigd mag worden.
     * Dit mag niet als er al personeel is gekoppeld (van de huidige externe WM).
     */
    get kanExterneWmWijzigen(): boolean {
        if (this.readonly) {
            return false;
        }
        // Als er geen externe WM is, mag je hem altijd zetten (mits niet readonly)
        if (!this.record.externe_werkmaatschappij) {
            return true;
        }
        // Als er wel een externe WM is, check of er personeel is gekoppeld.
        // We kijken in de record.personeel_planitem lijst.
        return !this.record.personeel_planitem?.some(ppi => ppi.personeel?.werkmaatschappij_id === this.record.externe_werkmaatschappij);
    }

    /**
     * Wij testen ook onze subcomponenten
     */
    override get isDirty(): boolean {
        return this.saveExtra || this.invullingComponent?.isDirty || super.isDirty; //saveExtra is standaard false, dus dat geeft dirty als true
    }

    override get recordBeschrijving(): string {
        return 'dit planitem';
    }

    ////////////////////////////////////////////////////////////////
    override get title(): string {
        let t: string;
        if (this.isExternItem && this.externeProjectInfo) {
            t = `Plan ${this.externeProjectInfo.projectomschrijving} (${this.externeProjectInfo.projectnaam})`;
        } else if (this.record?.project) {
            t = `Plan ${this.record.project.label}`;
        } else {
            t = `Plan`;
        }
        if (this.record.startdate) {
            t += ` ${moment(this.record.startdate).format('D-M-YY')}`;
        }
        return t;
    }

    /**
     * Overschrijf de canwrite van de parent, want leveranciers kunnen ook schrijven, zij het beperkt
     */
    override async canWrite(): Promise<boolean> {
        return this.isExterneBewerker || this.api.can('planitem', 'project') || (await super.canWrite()); //projectplanner mag ook schrijven
    }

    /**
     * overschrijf de canreadonly van parent, want aanvragers mogen niet schrijven als er al invulling is
     * @protected
     */
    protected async canReadonly(): Promise<boolean> {
        //als er invulling is, moet er personeelvast_planitem:full zijn om iets te kunnen saven
        //dit om te voorkomen dat iemand een al ingevuld item verplaatst
        /*
        Het probleem is hier dat daardoor dus geen invulling gewijzigd kan worden door een leverancier?
         */
        return (await super.canReadonly()) || (this.api.can('planitem', 'readproject') && (!this.api.can('planitem', ['full', 'project']))) || ((!!this.record.personeel_planitem?.length) && (!this.magPersoneelEdit));
    }


    async afterHaaldata(): Promise<void> {
        await super.afterHaaldata();

        //bepaal of dit een extern item is
        this.checkExtern();

        //fix the owner if needed
        this.fixOwner(); //and calculates mindate

        //make sure we have the right number of personeel rows en fit
        this.synchroniseerInvulling();

        //dingen die we niet doen bij leveranciers, omdat we niet bij die data kunnen komen
        if (!this.isExterneBewerker) {
            this.checkPersoneelskritiek(); //fix overlapcheck

            //series?
            await this.record.checkSeries(false, Project, this.magPersoneelEdit); //haal de ppi's ook op als we het mogen aanpassen, zie aftersave
            //als er een serie is, dan zijn dat de extra records voor invulling.
            //wij doen geen extra records voor een hele functierij, dat doet een apart scherm
            if (this.record.seriesItems.length) {
                this.extraRecords = this.record.seriesItems;
            }
        } else if (this.isExterneBewerker && !this.isExternItem) {
            //wel leverancier. Hoe is het met de lock?
            this.readonly = this.readonly || this.record.gelockt;
        }

        this.log.debug(`readOnly`, this.readonly, `magPersoneelEdit`, this.magPersoneelEdit, `magPersoneelRead`, this.magPersoneelRead, `isExterneBewerker`, this.isExterneBewerker, `magLog`, this.magLog);
    }

    /**
     * Fix de overlap- en afwezigheidskritiek check bij wijzigen van item (muv series) en na clonen van het record
     */
    checkPersoneelskritiek(): void {
        this.invullingComponent?.checkPersoneelskritiek();
    }

    /**
     * on project change
     */
    projectChange() {
        this.fixOwner(); //set owner through project_id
        //het is nooit meer een wisseling van werkmaatschappij
        //nu is alles mooi leeg
        this.invullingComponent?.fitPersoneel(); //zorg voor juiste personeel, gegeven werkmaatschappij vh project
    }

    /**
     * Datum of tijden zijn gewijzigd. Met een debounce werken we het een en ander bij
     */
    momentChange() {
        this.debouncer.set('momentChange', () => {
            //dan moet het personeel opnieuw gesorteerd worden
            this.checkPersoneelskritiek();
            this.invullingComponent?.fitPersoneel();
        }, 500);
    }

    /**
     * Determine if this item is ready to be saved
     */
    canSave(): boolean {
        if (this.readonly || this.shouldReload || this.form?.invalid) {
            return false;
        }

        //check specials: ngSelect
        if (!(this.record.project_id && this.record.mainFunction)) {
            return false;
        }
        //check series: at least one day
        // noinspection RedundantIfStatementJS
        if (this.planSeries.create && !Object.keys(this.planSeries.weekdays).some(day => this.planSeries.weekdays[day])) {
            return false;
        }

        return true;
    }

    roundCapacity() {
        if (this.record.capacity) {
            this.record.capacity = Math.round(this.record.capacity);
        }
    }

    /**
     * After getting lookup, or on change: make a set of relevant functions and a set of less relevant
     */
    splitFunctions() {
        this.relevantFunctions = [];
        this.irrelevantFunctions = [];
        for (let func of <Functie[]> this.lookups.functions) {
            if (this.record.hasExtrafunction(func) || this.record.mainFunction === func.id) {
                this.relevantFunctions.push(func);
            } else if (!this.readonly) {
                if (this.record.project?.heeftStandaardfunctie(func.id)) {
                    this.relevantFunctions.push(func);
                } else {
                    this.irrelevantFunctions.push(func);
                }
            }
        }
        // kopie bewaren voor de 'Alle functies' schakelaar
        this.relevanteFunctiesOngefilterd = [...this.relevantFunctions];
        // toepassen van eventuele filtertool op functies
        this.initFiltertoolKoppeling();
        this.herberekenFunctieFilter();
    }

    /**
     * Lijst met externe werkmaatschappijen, gefilterd zodat de eigen werkmaatschappij nooit gekozen kan worden
     */
    public get externeWerkmaatschappijen(): Werkmaatschappij[] {
        return (this.lookups?.werkmaatschappij as Werkmaatschappij[])?.filter(wm => wm.id !== this.record.project?.werkmaatschappij_id) || [];
    }

    override afterHaalLookups() {
        this.splitFunctions();
    }

    /**
     * Something changed in the function selection
     */
    functionsChanged() {
        this.splitFunctions();
        this.fitPersoneel();
    }

    /**
     * Initialiseer de koppeling met de filtertool op basis van de meegegeven input-id en vlag.
     * Abonneert op wijzigingen zodat de lijst met functies automatisch wordt bijgewerkt.
     */
    private initFiltertoolKoppeling(): void {
        // als filtering niet toegestaan is of geen id, verbreek koppeling
        if (!this.filtertoolFactory.canFiltertool() || !this.filtertoolId) {
            this.filtertool = null;
            this.negeerFilter = false;
            return;
        }
        // haal instantie op via factory
        this.filtertool = this.filtertoolFactory.opId(this.filtertoolId) ?? null;
        // luister naar toepassen en boommutes om te herberekenen
        // NB: KeyedTimeout voorkomt overmatige herberekening bij meerdere events kort na elkaar
        if (this.filtertool) {
            this.filtertool.toegepast$.subscribe(() => {
                this.herberekenFilterTimeout.set('f', () => this.herberekenFunctieFilter(), 50);
                this.herberekenFilterTimeout.set('p', () => this.herberekenPersoneelFilter(), 50);
            });
            this.filtertool.boomGewijzigd$.subscribe(() => {
                this.herberekenFilterTimeout.set('f', () => this.herberekenFunctieFilter(), 50);
                this.herberekenFilterTimeout.set('p', () => this.herberekenPersoneelFilter(), 50);
            });
        }
    }

    /**
     * Herbereken de gefilterde functielijst o.b.v. de actieve filtertool.
     * Als er geen actieve filter is, gebruik de ongefilterde lijst.
     */
    private herberekenFunctieFilter(): void {
        const bron = this.relevanteFunctiesOngefilterd || [];
        if (this.filtertool) {
            // filter via filterFunctie
            this.relevanteFunctiesGefilterd = bron.filter(f => this.filtertool!.filterFunctie(f));
        } else {
            this.relevanteFunctiesGefilterd = [...bron];
        }
    }

    /**
     * Herbereken de personeelslijst o.b.v. de filtertool. Houd rekening met negeerFilter en
     * zorg dat reeds gekozen personeelsleden in de invulling zichtbaar blijven.
     */
    private herberekenPersoneelFilter(): void {
        this.invullingComponent?.herberekenPersoneelFilter();
    }

    /**
     * Wijziging van de 'negeerFilter' schakelaar; herbereken de personeelslijst direct.
     */
    negeerFilterChanged(): void {
        // Herbereken beide lijsten zodat UI direct klopt
        this.herberekenPersoneelFilter();
        this.herberekenFunctieFilter();
    }

    /**
     * Geeft de lijst van functies terug die in de UI zichtbaar moet zijn.
     * Als er functies zijn weggefilterd, dan worden de reeds geselecteerde functies (hoofd- en extra functies)
     * altijd toegevoegd zodat ze selecteerbaar/zichtbaar blijven.
     *
     * Implementatie houdt de code eenvoudig en leesbaar: we nemen een basislijst,
     * lopen over de gekozen functie-id's en vullen aan waar nodig. Na een toevoeging
     * sorteren we op naam.
     */
    get visibleRelevantFunctions(): Functie[] {
        // basislijst: ofwel alle relevante functies, of de gefilterde subset (altijd als kopie)
        const basis = (this.negeerFilter || !this.functiesWeggefilterd)
            ? ([...(this.relevanteFunctiesOngefilterd || [])])
            : ([...(this.relevanteFunctiesGefilterd || [])]);

        // Als niets weggefilterd is (of we tonen alles), kan de basislijst direct terug
        if (this.negeerFilter || !this.functiesWeggefilterd) {
            return basis;
        }

        // Stel de lijst met gekozen functie-id's samen (hoofdfunctie + extra functies)
        const gekozenIds: number[] = [];
        if (this.record?.mainFunction) {
            gekozenIds.push(this.record.mainFunction);
        }
        for (const pif of (this.record?.planitem_function || [])) {
            if (pif?.functie_id) {
                gekozenIds.push(pif.functie_id);
            }
        }

        // Loop door de gekozen id's en vul de basis aan indien nodig
        let ietsToegevoegd = false;
        for (const fid of gekozenIds) {
            if (basis.some(f => f.id === fid)) {
                continue;
            }
            const gevonden = ((this.lookups?.functions as Functie[]) || []).find(x => x.id === fid);
            if (gevonden) {
                basis.push(gevonden);
                ietsToegevoegd = true;
            }
        }

        // Na aanvullen sorteren op naam (stabiel genoeg voor UI-doeleinden)
        if (ietsToegevoegd) {
            basis.sort((a, b) => a.naam.localeCompare(b.naam));
        }

        return basis;
    }

    /**
     * After load data of project change: calculte the mindate
     */
    calculateMindate() {
        //we allow edit of items in the past, so mindate is the begin date of the project
        if (this.record.project) {
            this.mindate = this.record.project.begindatum;
        } else {
            this.mindate = moment().format('YYYY-MM-DD'); //fallback
        }
    }

    /**
     * Add or remove the function from the extra functions
     * @param func
     */
    toggleExtrafunction(func: Functie) {
        if (this.readonly || this.isExterneBewerker) {
            return;
        }
        if (this.record.hasExtrafunction(func)) {
            this.record.planitem_function = this.record.planitem_function.filter(pif => pif.functie_id !== func.id);
        } else {
            this.record.planitem_function.push(PlanitemFunction.createNew(this.record, {
                functie_id: func.id,
                main: false
            }));
        }
        this.setDirty();
        this.functionsChanged();
    }

    /**
     * ux helper bij selectie leveranciers
     */

    leveranciersInWM(): Leverancier[] {
        return (<Leverancier[]> this.lookups.leverancier || []).filter(l => l.werkmaatschappij_id === this.record.project?.werkmaatschappij_id);
    }

    /**
     * Create a text to explain what we are going to do
     */
    planSeriesText(): string {
        let standaardzin = 'Vul dit item volledig in, kies hier minstens één werkdag en een einddatum. Bij het oplaan kopiëren we dit item dan naar de aangegeven werkdagen, met dezelfde functie, project, capaciteit, begin- en eindtijd en invulling. Als je een dienst wilt uitzetten zonder invulling, maak dan eerst de reeks zonder invulling, en bewerk daarna elke dienst.';
        if (this.form.invalid) {
            return standaardzin;
        }
        let datum = moment(this.record.startdate).format('D-M-Y');
        let einddatum = moment(this.planSeries.endDate).format('D-M-Y');
        let dagen = Object.keys(this.planSeries.weekdays).filter(day => this.planSeries.weekdays[day]).map(day => translated_days[day]);
        let weken = this.planSeries.everyNumWeeks > 1 ? `${this.planSeries.everyNumWeeks} weken` : 'week';
        if (!dagen.length) {
            return standaardzin;
        }
        return `We plannen deze functies en capaciteit op dit project op ${datum} van ${this.record.starttime} tot ${this.record.endtime}. Vervolgens plannen we hetzelfde op elke ${dagen.join(', ')} van elke ${weken}, totdat de begindatum van een item na ${einddatum} zou vallen. Ook de invulling wordt meegekopieerd. Controleer dus problemen en conflicten.`;
    }

    roundNumWeeks() {
        if (this.planSeries.everyNumWeeks) {
            this.planSeries.everyNumWeeks = Math.round(this.planSeries.everyNumWeeks);
        }
    }

    seriesdate(end: 'first' | 'last'): string {
        if (!this.record?.seriesItems?.length) {
            return '?';
        }
        let tag = `seriesdate_${end}`;
        if (this.record._tags[tag]) {
            return this.record._tags[tag];
        }
        let date = moment(this.record.seriesItems[end === 'first' ? 0 : this.record.seriesItems.length - 1].startdate).format('DD-MM-YYYY');
        this.record._tags[tag] = date;
        return date;
    }

    /////////////////////////////// planSeries /////////////////////////

    /**
     * Detach this item from the series (on save)
     */
    detachFromSeries() {
        this.record.resetSeries();
        this.extraRecords = null;
        this.api.toast('Dit item wordt uit de reeks verwijderd bij het opslaan');
    }

    canDeleteSeries(): boolean {
        return this.api.can(this.table, 'full');
    }

    /**
     * Delete the full series, except this one. Try every record, and skip the ones that can't be deleted
     */
    async deleteSeries() {
        if (!this.record?.seriesItems.length) {
            return;
        }
        let ids = this.record.seriesItems.map(pi => pi.id);
        if (this.record.id) {
            ids = ids.filter(id => id !== this.record.id);
        }
        if (!ids.length) {
            return;
        }
        if (await this.api.confirm('Verwijderen', `Hiermee verwijder je ${ids.length} items. We proberen elk item, maar het kan dat sommige niet door jou verwijderd mogen worden. Weet je zeker dat je wilt doorgaan?`)) {
            let mislukt: number = 0;
            for (let id of ids) {
                try {
                    await this.api.delete('planitem', id, true, false); //no toast
                } catch (_e) {
                    mislukt++;
                }
            }
            if (mislukt) {
                this.api.toast(`${mislukt} diensten konden niet verwijderd worden. Controleer deze dienst en de overige.`, 'warning');
            } else {
                this.api.toast('Alle diensten uit de reeks zijn vewijderd');
            }
            this.extraRecords = null; //geen seriesrecords meer
            await this.record.checkSeries(true, Project, this.magPersoneelEdit); //haal de ppi's ook op als we het mogen aanpassen, zie aftersave
        }

    }

    ////////////////////////////// personeel //////////////////////////////
    /**
     * Make sure there are enough personeel_planitem rows for the given capacity. Extra is not a problem (here)
     * @private
     */
    fixPersoneelrows() {
        this.invullingComponent?.fixPersoneelrows();
    }

    /**
     * Tag personeel for fitness for the functions
     */
    fitPersoneel() {
        this.invullingComponent?.fitPersoneel();
    }

    personeel(id: number): Personeel {
        return this.invullingComponent?.personeel(id);
    }


    leverancier(id: number): Leverancier {
        return this.invullingComponent?.leverancier(id);
    }

    /**
     * Overschrijf save() zodat we onthouden of de gebruiker via "Opslaan + melding" heeft gekozen.
     * @param melding true als de automelding-engine getriggerd moet worden (knop "Opslaan + melding")
     */
    override async save(melding: boolean = false): Promise<void> {
        this.meldingBijOpslaan = melding;
        await super.save();
    }

    protected savedata(): Datarecord {
        //filter personeel_planitem before saving
        this.invullingComponent?.fixPersoneelrows();
        this.record.personeel_planitem = this.record.personeel_planitem.filter(ppi => ppi.personeelvast_id);
        if (!this.record.functionalias?.length) {
            //reset to null
            this.record.functionalias = null;
        }
        let record = super.savedata();
        if (this.meldingBijOpslaan) {
            record.wijzigmelding = DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss');
        } else {
            delete record.wijzigmelding;
        }
        //leveranciers mogen niks, behalve personeel
        if (this.isExterneBewerker) {
            if (this.isNieuw) {
                return; //mag niet
            }
            //alles erui behalve id en ppi
            const ppiconnected = record._connected.find(c => c.table === 'personeelvast_planitem');
            record = {
                id: record.id,
                _connected: [ppiconnected]
            };
        }
        //mensen met readonly toegang tot personeel mogen juist niet ppi
        if (!this.magPersoneelEdit) {
            record._connected = (record._connected || []).filter(c => c.table !== 'personeelvast_planitem');
        }
        return record;
    }


    /////lookups

    protected async afterSave(): Promise<void> {
        //series?
        if (this.planSeries.create) {
            //we have to create a serieskey
            const chars = 'abcdefghijklmnopqrstuvwxyz';
            let serieskey: string = `${Date.now()}`;
            while (serieskey.length < 20) {
                serieskey += chars[Math.floor(Math.random() * chars.length)];
            }
            let data = this.savedata(); //again, we use the base data, not the model

            data['serieskey'] = serieskey;
            let save = [data];
            //clone and cleanup, so we have a cloneable version
            data = cloneDeep(data);
            delete data['id']; //next ones will be new
            //let's skip personeel
            let newconnected = [];
            for (let c of data._connected || []) {
                //functie en ppi gaan gewoon door, als nieuw, de rest niet
                if (c.table === 'planitem_function' || c.table === 'personeelvast_planitem') {
                    //remove ids
                    for (let r of c.data) {
                        delete r['id'];
                    }
                    newconnected.push(c);
                }
            }
            data._connected = newconnected;
            this.log.debug(`save serie`, data);

            let stopdate = moment(this.planSeries.endDate).startOf('day').add(1, 'day'); //must be before this date
            let date = moment(data['startdate']).startOf('day').add(1, 'day'); //not the start date


            do {
                let curday = date.isoWeekday() - 1; //0=monday
                if (this.planSeries.weekdays[daykeys[curday]]) {
                    //add this date
                    data = cloneDeep(data);
                    data['startdate'] = date.format('YYYY-MM-DD');
                    save.push(data);
                }
                //try next day
                date.add(1, 'day');
                //did we just do sunday?
                if (curday === 6 && this.planSeries.everyNumWeeks > 1) {
                    //skip weeks
                    date.add(this.planSeries.everyNumWeeks - 1, 'weeks');
                }
            } while (date.isBefore(stopdate));
            //save!
            try {
                await this.api.save({
                    table: this.table,
                    data: save
                }); //doesnt give notifications because we are still in action 'save'
                this.api.toast(`Aangemaakte items: ${this.isNieuw ? save.length : save.length - 1}`); //isNieuw still works in afterSave
            } catch (_e) {
                //already logged and toasted
            }

        }
        //als we geen nieuwe reeks maken, en er is iets gewijzigd aan de invulling, dan voeren we het door
        else if (this.invullingComponent?.isDirty && (!this.isExterneBewerker) && this.magPersoneelEdit && (!!this.extraRecords?.length) && (this.saveExtra)) {
            this.log.warn('SAVE ALLES');
            //we moeten de extra items saven met de invulling van dit item
            //voor zover ze ná dit item liggen
            const vanaf = this.record.startdatetime;
            let gewijzigd = 0;
            let mislukt = 0;
            let ingediend = 0;
            try {
                this.api.block(); //we doen een block om het geheel
                for (let extra of this.extraRecords) {
                    //extra hetzelfde?
                    if (extra.personeel_planitem.length === this.record.personeel_planitem.length &&
                        extra.personeel_planitem.every(extrappi => this.record.personeel_planitem.some(recppi =>
                                recppi.personeelvast_id === extrappi.personeelvast_id &&
                                (recppi.leverancier_id === null || recppi.leverancier_id === extrappi.leverancier_id)
                            )
                        )
                    ) {
                        continue; //niks te doen
                    }

                    if (extra.id !== this.record.id && extra.startdatetime >= vanaf) {
                        if (extra.personeel_planitem?.some(ppi => ppi.inkooporder || ppi.ingediend)) {
                            ingediend++; //skip ingediende dienst
                            continue;
                        }

                        extra.personeel_planitem = this.record.personeel_planitem.map(ppi =>
                            PersoneelPlanitem.createNew(extra, {
                                personeelvast_id: ppi.personeelvast_id,
                                planitem_id: extra.id,
                                leverancier_id: ppi.leverancier_id
                            })
                        );
                        //en dit opslaan
                        try {
                            await extra.save();
                            gewijzigd++;
                        } catch {
                            //toast is al geweest
                            mislukt++;
                        }
                    }
                }
            } finally {
                this.api.unblock();
            }
            if (mislukt) {
                this.api.toast(`De invulling van ${mislukt} ${mislukt > 1 ? 'diensten' : 'dienst'} kon niet aangepast worden. Controleer de reeks handmatig.`, 'warning');

            }
            if (ingediend) {
                this.api.toast(`De invulling van  ${ingediend} ${ingediend > 1 ? 'diensten' : 'dienst'} is niet aangepast omdat hier al werkbonnen of inkooporders voor bestaan. Controleer de reeks handmatig.`, 'warning');
            }
            if (gewijzigd && (!mislukt) && (!ingediend)) {
                this.api.toast(`De invulling van ${gewijzigd} ${gewijzigd > 1 ? 'diensten' : 'dienst'} uit de reeks (na de huidige) is aangepast.`);
            }
        }

        await super.afterSave();
    }

    ///////////////////////////////////////////////////////////////////////
    /**
     * Geeft een label terug voor het externe project (omschrijving + naam)
     */
    getExternProjectLabel(): string {
        if (!this.externeProjectInfo) {
            return this.record.project?.label || 'Onbekend project';
        }
        if (this.externeProjectInfo.projectomschrijving?.length) {
            return `${this.externeProjectInfo.projectomschrijving} (${this.externeProjectInfo.projectnaam})`;
        }
        return this.externeProjectInfo.projectnaam || 'Onbekend project';
    }

    /**
     * Geeft de naam van de externe werkmaatschappij terug
     */
    getExternWerkmaatschappijNaam(): string {
        return this.externeProjectInfo?.werkmaatschappijnaam || 'Andere werkmaatschappij';
    }

    /**
     * Fix the owner of the record after update of data or project change
     * @private
     */
    private fixOwner() {
        if (!this.record.project_id) {
            this.record._owner = null; //no project
        }

        if (this.isExternItem && this.externeProjectInfo) {
            // Voor externe items zoeken we het placeholder project (dummy project)
            // waarbij de externe_werkmaatschappij overeenkomt met de bron-werkmaatschappij.
            const placeholder = this.projects.find(pr => pr.externe_werkmaatschappij === this.externeProjectInfo.werkmaatschappij);
            if (placeholder) {
                this.record._owner = placeholder;
            }
        } else if (this.record.project?.id !== this.record.project_id) {
            this.record._owner = this.projects.find(pr => pr.id === this.record.project_id);
        }
        this.calculateMindate(); //recalculate the min date for the item
        this.splitFunctions(); //set relevant functions
    }

    protected readonly DateTime = DateTime;

    /**
     * logtab geklikt, we halen de log op
     */
    async initLogtab() {
        await this.record.vulLog();
    }

}
